import { useEffect, useRef } from 'react';
import * as THREE from 'three';

// Detect WebGL2 once so callers can fall back gracefully.
export const supportsWebGL2 = (() => {
  try {
    const canvas = document.createElement('canvas');
    return !!(window.WebGL2RenderingContext && canvas.getContext('webgl2'));
  } catch {
    return false;
  }
})();

// Vanilla three.js (imperative) — avoids react-three-fiber so the visual-edits
// babel plugin's injected DOM attributes don't reach three.js elements.
const BeetleEarthScene = ({ onEnter }) => {
  const mountRef = useRef(null);
  const onEnterRef = useRef(onEnter);
  onEnterRef.current = onEnter;

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let width = mount.clientWidth || window.innerWidth;
    let height = mount.clientHeight || window.innerHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);

    // Pull the camera back on portrait/mobile so the full globe fits horizontally
    // (a vertical-FOV perspective camera crops the sides on narrow screens otherwise).
    const fitCamera = () => {
      const aspect = width / height;
      const fovRad = (camera.fov * Math.PI) / 180;
      const R = 2.15; // globe radius + margin
      const distV = R / Math.tan(fovRad / 2); // fit vertically
      const distH = R / (Math.tan(fovRad / 2) * aspect); // fit horizontally
      camera.position.set(0, 0, Math.max(6, distV, distH));
      camera.updateProjectionMatrix();
    };
    fitCamera();

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setSize(width, height);
    renderer.domElement.style.cursor = 'pointer';
    renderer.domElement.style.display = 'block';
    mount.appendChild(renderer.domElement);

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff, 0.9));
    const dir = new THREE.DirectionalLight(0xffffff, 1.1);
    dir.position.set(3, 4, 5);
    scene.add(dir);
    const rim = new THREE.DirectionalLight(0x7b6cff, 0.3);
    rim.position.set(-4, -2, 2);
    scene.add(rim);

    const loader = new THREE.TextureLoader();

    // Earth group (tilted) — large and CENTERED, the hero centerpiece
    const earthGroup = new THREE.Group();
    earthGroup.rotation.set(0.22, 0, 0.1);
    earthGroup.position.set(0, 0.1, -0.2);
    earthGroup.scale.setScalar(1.2);
    scene.add(earthGroup);

    // Atmosphere
    const atmosphere = new THREE.Mesh(
      new THREE.SphereGeometry(1.7, 32, 32),
      new THREE.MeshBasicMaterial({ color: 0x5fd6e6, transparent: true, opacity: 0.12, side: THREE.BackSide })
    );
    atmosphere.scale.setScalar(1.14);
    earthGroup.add(atmosphere);

    // Earth
    const earth = new THREE.Mesh(
      new THREE.SphereGeometry(1.7, 48, 48),
      new THREE.MeshStandardMaterial({ roughness: 0.9, metalness: 0.05, color: 0x88aacc })
    );
    earthGroup.add(earth);
    loader.load('/almanac/planet.png', (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      earth.material.map = tex;
      earth.material.color.set(0xffffff);
      earth.material.needsUpdate = true;
    });

    // Beetle billboard — small, at the base of the globe, spinning it with its hind legs
    const beetleMat = new THREE.MeshBasicMaterial({ transparent: true, alphaTest: 0.05, depthWrite: false });
    const beetle = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), beetleMat);
    beetle.position.set(-1.8, -1.55, 0.8);
    beetle.scale.setScalar(0.6);
    scene.add(beetle);
    loader.load('/almanac/beetle.png', (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      beetleMat.map = tex;
      beetleMat.needsUpdate = true;
    });

    // Starfield
    const starCount = 700;
    const positions = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount; i++) {
      const r = 14 + Math.random() * 16;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi) - 6;
    }
    const starGeo = new THREE.BufferGeometry();
    starGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const stars = new THREE.Points(
      starGeo,
      new THREE.PointsMaterial({ color: 0xffffff, size: 0.05, sizeAttenuation: true, transparent: true, opacity: 0.8 })
    );
    scene.add(stars);

    // Animation
    const clock = new THREE.Clock();
    let raf;
    const animate = () => {
      const delta = clock.getDelta();
      const t = clock.elapsedTime;
      earth.rotation.y += delta * 0.06;
      atmosphere.rotation.y += delta * 0.06;
      stars.rotation.y += delta * 0.01;
      beetle.position.y = -1.55 + Math.sin(t * 1.6) * 0.03;
      beetle.rotation.z = -0.05 + Math.sin(t * 1.6) * 0.07;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    const handleClick = () => onEnterRef.current?.();
    renderer.domElement.addEventListener('click', handleClick);

    const handleResize = () => {
      width = mount.clientWidth || window.innerWidth;
      height = mount.clientHeight || window.innerHeight;
      camera.aspect = width / height;
      fitCamera();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', handleResize);
      renderer.domElement.removeEventListener('click', handleClick);
      renderer.dispose();
      starGeo.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} style={{ width: '100%', height: '100%' }} data-testid="beetle-earth-canvas" />;
};

export default BeetleEarthScene;
